import { Box, alpha, lighten, useMediaQuery, useTheme } from '@mui/material';
import { FC, ReactNode, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { PrivateRoute } from 'components/authentication/PrivateRoutes';
import slug from 'constants/slug';
import { companyDetails } from 'redux/retailers/selector';
import { useAppSelector } from 'redux/redux';
import Header from './Header';
import Sidebar from './Sidebar';

interface SidebarLayoutProps {
  children?: ReactNode;
  requiredRoles?: string[];
}

const SidebarLayout: FC<SidebarLayoutProps> = ({ requiredRoles }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const companyData = useAppSelector(companyDetails);
  /** sidebar (and its Miele-only modules) is confined to the Miele flavour */
  const showSidebar = companyData?.slug === slug.MIELE;
  /** sidebar visibility — default open on desktop, closed (overlay) on mobile */
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <>
      <PrivateRoute requiredRoles={requiredRoles}>
        <Box
          sx={{
            flex: 1,
            height: '100%',

            '.MuiPageTitle-wrapper': {
              background:
                theme.palette.mode === 'dark'
                  ? theme.colors.alpha.trueWhite[5]
                  : theme.colors.alpha.white[50],
              marginBottom: `${theme.spacing(4)}`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? `0 1px 0 ${alpha(
                      lighten(theme.colors.primary.main, 0.7),
                      0.15
                    )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                  : `0px 2px 4px -3px ${alpha(
                      theme.colors.alpha.black[100],
                      0.1
                    )}, 0px 5px 12px -4px ${alpha(
                      theme.colors.alpha.black[100],
                      0.05
                    )}`,
            },
          }}
        >
          <Header
            sidebarOpen={showSidebar && sidebarOpen}
            onToggleSidebar={toggleSidebar}
            showToggle={showSidebar}
          />
          {showSidebar && (
            <Sidebar open={sidebarOpen} onClose={closeSidebar} />
          )}
          <Box
            sx={{
              position: 'relative',
              zIndex: 5,
              display: 'block',
              flex: 1,
              pt: `${theme.header.height}`,
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              [theme.breakpoints.up('lg')]: {
                ml: showSidebar && sidebarOpen ? `${theme.sidebar.width}` : 0,
              },
            }}
          >
            <Box display="block">
              <Outlet />
            </Box>
          </Box>
        </Box>
      </PrivateRoute>
    </>
  );
};

export default SidebarLayout;
